import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Typography, Box } from '@mui/material';

import CoreVirtualizedTable from '../ui/core/CoreVirtualizedTable';
import { FileHistory, UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS, UploadHistoryTableColumnId } from '../../types/claims';
import { useUploadHistoryStore } from '../../hooks/useUploadHistoryStore';
import { ColumnFiltersConfig, SimpleColumnDef } from '../../types/table';
import { formatInstant } from '../helpers/dataHelper';
import { formatLocalDate } from '../../stores/UploadHistoryStore';

const STATUS_FILTER_OPTIONS = ['PROCESSED', 'SKIPPED', 'ERROR', 'UPLOADED', 'PROCESSING'];
const EDI_TYPE_FILTER_OPTIONS = ['CLAIM', 'PAYMENT', 'CLAIM_STATUS'];

const UploadHistoryTable: React.FC = observer(() => {
  const store = useUploadHistoryStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [tableHeight, setTableHeight] = useState('500px');

  // Setup ResizeObserver with callback ref
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    if (node) {
      containerRef.current = node;
      
      const updateHeight = () => {
        const height = node.clientHeight;
        if (height > 0) {
          setTableHeight(`${height}px`);
        }
      };

      // Use ResizeObserver to detect container size changes
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          if (height > 0) {
            setTableHeight(`${height}px`);
          }
        }
      });
      
      resizeObserver.observe(node);
      resizeObserverRef.current = resizeObserver;

      // Initial height calculation after layout is complete
      // Use multiple requestAnimationFrame to ensure layout is painted
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            updateHeight();
            // Also try after a small timeout as backup
            setTimeout(updateHeight, 100);
          });
        });
      });
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Load file list on mount and when filters change (sorting is applied in handleSortingChange)
  useEffect(() => {
    store.loadFileList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.statusFilter, store.ediTypeFilter]);

  const columns: SimpleColumnDef<FileHistory>[] = useMemo(
    () => [
      {
        id: 'uploaded_at',
        header: 'Date',
        accessorKey: 'uploaded_at',
        minWidth: 80,
        cell: (value) => (
          <Typography sx={{ color: 'text.primary' }}>
            {value ? formatInstant(value) : '-'}
          </Typography>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        minWidth: 70,
        cell: (value) => {
          const status = value;
          return (
            <Typography
              sx={{
                color:
                  status === 'PROCESSED'
                    ? '#4caf50'
                    : status === 'ERROR'
                    ? '#f44336'
                    : status === 'SKIPPED'
                    ? '#ff9800'
                    : '#2196f3',
                fontWeight: 500,
              }}
            >
              {status}
            </Typography>
          );
        },
      },
      {
        id: 'edi_object_type',
        header: 'EDI Type',
        accessorKey: 'edi_object_type',
        minWidth: 70,
        cell: (value) => (
          <Typography sx={{ color: 'text.primary' }}>
            {value || 'N/A'}
          </Typography>
        ),
      },
      {
        id: 'claims_found',
        header: 'Claims#',
        accessorKey: 'claims_found',
        minWidth: 70,
        cell: (value) => (
          <Typography sx={{ color: 'text.primary' }}>
            {value}
          </Typography>
        ),
      },
      {
        id: 'claims_processed',
        header: 'Processed',
        accessorKey: 'claims_processed',
        minWidth: 70,
        cell: (value) => (
          <Typography sx={{ color: 'text.primary' }}>
            {value}
          </Typography>
        ),
      },
      {
        id: 'file_name',
        header: 'File Name',
        accessorKey: 'file_name',
        minWidth: 100,
        cell: (value) => (
          <Typography sx={{ color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {value}
          </Typography>
        ),
      },
      {
        id: 'uploaded_by',
        header: 'Uploaded By',
        accessorKey: 'uploaded_by',
        minWidth: 100,
        cell: (value) => (
          <Typography sx={{ color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {value || 'N/A'}
          </Typography>
        ),
      },
    ],
    []
  );

  // Visible columns, pinned (required) columns first — the order comes from the
  // store so the CSV export uses exactly the same columns in the same order.
  const visibleColumns = useMemo(
    () =>
      store.visibleColumnIds
        .map((id) => columns.find((col) => col.id === id))
        .filter((col): col is SimpleColumnDef<FileHistory> => !!col),
    [columns, store.visibleColumnIds]
  );

  const handleSortingChange = useCallback(
    (newSorting: typeof store.sorting) => {
      store.setSorting(newSorting);
      store.loadFileList(undefined, 1, true, false, true);
    },
    [store]
  );

  const getColumnFilterOptions = useCallback((columnId: string): string[] => {
    switch (columnId) {
      case 'status':
        return STATUS_FILTER_OPTIONS;
      case 'edi_object_type':
        return EDI_TYPE_FILTER_OPTIONS;
      default:
        return [];
    }
  }, []);

  const columnFilters: ColumnFiltersConfig = useMemo(
    () => ({
      filters: {
        uploaded_at: {
          operator: 'and',
          values: [formatLocalDate(store.dateRange.start), formatLocalDate(store.dateRange.end)],
        },
        status: { operator: 'and', values: store.statusFilter ? [store.statusFilter] : [] },
        edi_object_type: { operator: 'and', values: store.ediTypeFilter ? [store.ediTypeFilter] : [] },
        file_name: { operator: 'and', values: store.fileNameFilter ? [store.fileNameFilter] : [] },
      },
      getOptions: getColumnFilterOptions,
      onApply: (columnId, values) => {
        switch (columnId) {
          case 'status':
            store.setStatusFilter(values[0] ?? null);
            break;
          case 'edi_object_type':
            store.setEdiTypeFilter(values[0] ?? null);
            break;
          case 'file_name':
            store.setFileNameFilter(values[0] ?? null);
            break;
          case 'uploaded_at': {
            const [start, end] = values;
            const effectiveStart = start || end;
            const effectiveEnd = end || start;
            if (effectiveStart && effectiveEnd) {
              store.applyCustomDateRange(effectiveStart, effectiveEnd);
            }
            break;
          }
          default:
            break;
        }
        store.loadDailyStats();
        store.loadFileList();
      },
      onClear: (columnId) => {
        switch (columnId) {
          case 'status':
            store.setStatusFilter(null);
            break;
          case 'edi_object_type':
            store.setEdiTypeFilter(null);
            break;
          case 'file_name':
            store.setFileNameFilter(null);
            break;
          case 'uploaded_at':
            store.clearDateRangeFilter();
            break;
          default:
            break;
        }
        store.loadDailyStats();
        store.loadFileList();
      },
      filterableColumns: ['uploaded_at', 'status', 'edi_object_type', 'file_name'],
      columnFilterTypes: {
        uploaded_at: 'dateRange',
        status: 'list',
        edi_object_type: 'list',
        file_name: 'text',
      },
      isColumnPinned: store.isColumnRequired,
      onTogglePin: (columnId: string) => store.toggleRequiredColumn(columnId as UploadHistoryTableColumnId),
      canPinColumn: (columnId: string) => !UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS.find((col) => col.id === columnId)?.mandatory,
    }),
    [store.dateRange, store.statusFilter, store.ediTypeFilter, store.fileNameFilter, getColumnFilterOptions, store.loadDailyStats, store.loadFileList, store.setStatusFilter, store.setEdiTypeFilter, store.setFileNameFilter, store.applyCustomDateRange, store.clearDateRangeFilter, store.isColumnRequired, store.toggleRequiredColumn]
  );

  if (store.isLoading && !store.fileList) {
    return (
      <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        Loading...
      </Typography>
    );
  }

  const handleRowClick = (file: FileHistory) => {
    store.openFileDetail(file.id);
  };

  return (
    <Box ref={setContainerRef} sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <CoreVirtualizedTable<FileHistory>
        isResizable
        pinColumns
        emptyMessage='No files found'
        data={store.fileList ?? []}
        columns={visibleColumns}
        sorting={store.sorting}
        hasMore={store.hasMore}
        isLoadingMore={store.isDataLoading}
        onLoadMore={() => store.loadFileList(undefined, store.fileListPage + 1, undefined, true)}
        onSortingChange={handleSortingChange}
        height={tableHeight}
        containerSx={{ height: '100%' }}
        onRowClick={handleRowClick}
        selectedRowId={store.selectedFile?.id}
        minWidth={1000}
        columnFilters={columnFilters}
        disableFirstOfTypeStyles
      />
    </Box>
  );
});

export default UploadHistoryTable;

